/**
 * Local-development bypass.
 *
 * This is the webapp analogue of iq-backend's `APP_ENVIRONMENT=local` switch in
 * `config.py`: a single flag that lets the app run against ONLY the local
 * backend (`localhost:8000`) without depending on the shared dev auth stack
 * (`dev-user-service`, `dev-subscription-service`) or the accounts login flow.
 *
 * Activated ONLY by the `localdev` build mode — i.e. `pnpm local`
 * (`vite --mode localdev`). This is safe by construction: every real deployment
 * builds with `--mode development`/`--mode production` (and tests run in
 * `test` mode), so `IS_LOCAL_DEV` is false everywhere except an explicit local
 * run. There is no env flag to accidentally commit and ship a bypass to a
 * deployed environment.
 *
 * Local config (Firebase keys, the localhost backend URL) lives in
 * `.env.localdev`, which Vite loads only in `localdev` mode — so it never
 * affects `pnpm dev` or any deployed build.
 *
 * What it does when active:
 *  - `resolveApiUrl()` rewrites every non-IQ service URL to the local backend.
 *  - A synthetic authenticated session (`LOCAL_DEV_*`) is injected by
 *    `AuthContext`, and `useUserAuth` skips the shared-cookie → login redirect.
 *
 * Nothing here is a security control — it only ever short-circuits in a local
 * dev build, never in production.
 */

import type { User } from "firebase/auth";
import type { IUserInfo } from "@/services/apis/authApi";
import type { UserCookie } from "@/types/cookies";
import type { IQAccess } from "@/hooks/useRbac";
import { FEATURE_PERMISSION_MAPPING, type PermissionKey } from "@/constants/rbac";

/**
 * True only under the `localdev` build mode (`pnpm local` →
 * `vite --mode localdev`). Tied to the build mode rather than an env var so it
 * can never be flipped on by a committed env file in a dev/prod deploy. (Vite
 * reserves the bare `local` mode name, so the mode is `localdev`.)
 */
export const IS_LOCAL_DEV = import.meta.env.MODE === "localdev";

/** The local backend everything points at in local-dev mode. */
export const LOCAL_BACKEND_URL =
  (import.meta.env.VITE_IQ_API_URL as string) || "http://localhost:8000";

/**
 * In local-dev mode, route any service URL at the local backend; otherwise
 * return the configured dev/prod URL untouched. Use this to wrap every
 * `VITE_*_API_URL` read so the app depends only on `localhost:8000` locally.
 */
export const resolveApiUrl = (url?: string): string =>
  IS_LOCAL_DEV ? LOCAL_BACKEND_URL : (url as string);

/**
 * Synthetic user details that stand in for the `saras_user_details` cookie
 * (normally populated by the accounts login flow). Override via env if you need
 * a specific company/user for local testing.
 */
export const LOCAL_DEV_USER: UserCookie = {
  userId: (import.meta.env.VITE_LOCAL_DEV_USER_ID as string) || "1",
  firstName: "Local",
  lastName: "Dev",
  email:
    (import.meta.env.VITE_LOCAL_DEV_EMAIL as string) || "local-dev@example.com",
  company: {
    columnHashingSal: "",
    name: "Local Dev Co",
    companyId: Number(import.meta.env.VITE_LOCAL_DEV_COMPANY_ID) || 1,
  },
} as IUserInfo;

/**
 * Minimal stand-in for the Firebase `currentUser`. The app only ever checks it
 * for truthiness (gating queries) and occasionally calls `getIdToken()`, so a
 * lightweight shim is enough — we never reach a real Firebase backend locally.
 */
export const LOCAL_DEV_FIREBASE_USER = {
  uid: "local-dev-uid",
  email: LOCAL_DEV_USER.email,
  displayName: `${LOCAL_DEV_USER.firstName} ${LOCAL_DEV_USER.lastName}`,
  emailVerified: true,
  isAnonymous: false,
  getIdToken: () => Promise.resolve("local-dev-token"),
  getIdTokenResult: () =>
    Promise.resolve({ token: "local-dev-token", claims: {} }),
} as unknown as User;

/** Full-access RBAC grant so every gated route renders in local dev. */
export const LOCAL_DEV_ROLE_ACCESS: IQAccess = (
  Object.keys(FEATURE_PERMISSION_MAPPING) as PermissionKey[]
).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {} as IQAccess);

// ── Dev-only persona override ─────────────────────────────────────────────────
// So the product owner can SEE the end-user persona on `pnpm local` (localhost is
// otherwise always the all-granted creator/admin persona). Consulted ONLY when
// IS_LOCAL_DEV — every selector below short-circuits on that flag first, so in a
// real dev/prod build this whole block is inert (the localStorage read is never
// reached and tree-shakes to a constant "creator").

/** localStorage key holding the local-dev persona override. */
export const LOCAL_DEV_PERSONA_KEY = "iq-localdev-persona";

/** The two personas the local-dev override can stand in for. */
export type LocalDevPersona = "creator" | "enduser";

/**
 * The minimal "just an end user" grant: they can use the app and chat, but have
 * NONE of the creator/admin/management surfaces (dashboards workspace, godview,
 * adoption metrics, admin feedback, user management). Mirrors what
 * a real end-user token would resolve to via `hasAppPermision`.
 *
 * Dashboards/use-cases: reads only — they can open what's shared with them but
 * cannot author. `SHARE_OWN_ORG` is granted to model an IQAdmin-style consumer so
 * the consume-share button stays exercisable under `pnpm local`.
 * Integrations: read only — omit WRITE_INTEGRATIONS so `pnpm local` enduser
 * sees the same read-only Slack cards as a real IQUser without write perms.
 */
const LOCAL_DEV_ENDUSER_GRANTS: PermissionKey[] = [
  "APP_PERMISSION",
  "READ_THREADS",
  "WRITE_THREADS",
  "READ_DASHBOARD",
  "READ_USECASE",
  "SHARE_OWN_ORG",
  "READ_INTEGRATIONS",
];
export const LOCAL_DEV_ROLE_ACCESS_ENDUSER: IQAccess = (
  Object.keys(FEATURE_PERMISSION_MAPPING) as PermissionKey[]
).reduce((acc, key) => {
  acc[key] = LOCAL_DEV_ENDUSER_GRANTS.includes(key);
  return acc;
}, {} as IQAccess);

/**
 * The active local-dev persona. Reads `localStorage[LOCAL_DEV_PERSONA_KEY]` — but
 * ONLY under IS_LOCAL_DEV, so it is a no-op ("creator") in every real build. A
 * page RELOAD is required for a change to take effect (AuthContext/route gates
 * read this once per render, not reactively). Anything other than the exact value
 * `"enduser"` (including absent) means the default all-granted creator persona.
 */
export function getLocalDevPersona(): LocalDevPersona {
  if (!IS_LOCAL_DEV) return "creator";
  try {
    return localStorage.getItem(LOCAL_DEV_PERSONA_KEY) === "enduser"
      ? "enduser"
      : "creator";
  } catch {
    // No localStorage (SSR/edge) — fall back to the default persona.
    return "creator";
  }
}

/** True only when running local-dev AND the persona override is set to end-user. */
export const isLocalDevEndUserPersona = (): boolean =>
  getLocalDevPersona() === "enduser";

/**
 * The role-access grant AuthContext injects in local dev — the end-user set when
 * the persona override is on, otherwise the full creator/admin grant.
 */
export const resolveLocalDevRoleAccess = (): IQAccess =>
  isLocalDevEndUserPersona() ? LOCAL_DEV_ROLE_ACCESS_ENDUSER : LOCAL_DEV_ROLE_ACCESS;
